// src/interfaces/user/QuoteReview.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import { calculateDeliveryCost } from '../../components/common/deliveryCalculator';
import AddressBookModal from '../../components/common/AddressBookModal';

const QuoteReview = ({ onClose }) => {
  const {
    currentQuote,
    removeProductFromQuote,
    updateProductInQuote,
    addQuote,
    clearCurrentQuote,
    requestUploadForQuote
  } = useQuotes();
  
  const { currentUser, hasRole } = useAuth();

  // All hooks must be called before any conditional logic
  const [quoteName, setQuoteName] = useState('');
  const [clientName, setClientName] = useState('');
  const [notes, setNotes] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [dateRequired, setDateRequired] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [submittedQuote, setSubmittedQuote] = useState(null);
  const [successCountdown, setSuccessCountdown] = useState(10);
  const successCloseGuardRef = useRef(false);
  const enterSubmitRef = useRef(false);

  // Delivery-related state
  const [deliveryOption, setDeliveryOption] = useState('collection');
  const [deliveryAddressText, setDeliveryAddressText] = useState('');
  const [expressSubOption, setExpressSubOption] = useState(null);

  // Address book state
  const [showAddressBookModal, setShowAddressBookModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Multi-address delivery state
  const [numberOfAddresses, setNumberOfAddresses] = useState(2);
  const [manualAddresses, setManualAddresses] = useState('');

  // Rename functionality state
  const [renamingItemId, setRenamingItemId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const multiPieceGroupMeta = useMemo(() => {
    const map = new Map();

    currentQuote.items.forEach((item, index) => {
      if (!item.multiPieceGroupId) {
        return;
      }

      const existing = map.get(item.multiPieceGroupId);
      const summary = item.multiPieceGroupSummary || existing?.summary || null;

      map.set(item.multiPieceGroupId, {
        name: item.multiPieceGroupName || summary?.groupName || existing?.name || item.jobName,
        count:
          item.multiPieceGroupCount ||
          summary?.totalPieces ||
          existing?.count ||
          1,
        firstIndex: existing ? Math.min(existing.firstIndex, index) : index,
        summary,
      });
    });

    return map;
  }, [currentQuote.items]);

  const totalProductsPrice = currentQuote.items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);

  // Calculate delivery costs
  // Delivery cost calculation is based on items already in the quote
  const expressDeliveryDetails = calculateDeliveryCost(currentQuote.items, 'express', expressSubOption);
  const expressDeliveryCost = expressDeliveryDetails.cost;
  const hasOversizedItems = expressDeliveryDetails.hasOversizedItems; // Get from express details
  
  // Check if we have products to submit
  const hasProducts = currentQuote.items.length > 0;

  // Determine if the form should be displayed (items in quote OR submission was successful)
  const displayForm = currentQuote.items.length > 0 || isSubmissionSuccessful;

  // Calculate delivery costs
  let deliveryCost = 0;
  if (deliveryOption === 'express') {
    deliveryCost = expressDeliveryCost;
  } else if (deliveryOption === 'multi-address') {
    deliveryCost = numberOfAddresses * 9.5; // £9.50 per address
  }

  const finalTotalPrice = totalProductsPrice + deliveryCost;

  // Check if delivery address is filled when needed
  const isDeliveryAddressValid = () => {
    if (deliveryOption === 'collection') return true;
    if (deliveryOption === 'multi-address') {
      // For >4 addresses, user will upload document so no textarea validation needed
      if (numberOfAddresses > 4) {
        return numberOfAddresses >= 2;
      }
      // For ≤4 addresses, require textarea to be filled
      return manualAddresses.trim() !== '' && numberOfAddresses >= 2;
    }
    if (deliveryOption === 'express') {
      return deliveryAddressText.trim() !== '';
    }
    return true;
  };

  // Handle address selection from address book
  const handleAddressSelected = (address) => {
    setSelectedAddress(address);

    // Format address as multi-line text
    const addressText = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.postcode,
      address.delivery_instructions
    ].filter(Boolean).join('\n');

    setDeliveryAddressText(addressText);

    // Update client name and company name fields
    setClientName(address.customer_name);
    setCompanyName(address.company_name || '');
  };

  // Handle adding new address
  const handleAddNewAddress = () => {
    // For now, just close the modal and let user manually enter
    // In a full implementation, you might want to open the AddressBookManager
    setShowAddressBookModal(false);
  };

  // Handle item rename
  const handleRenameStart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    setRenamingItemId(product.id);
    setRenameValue(product.jobName);
  };

  const handleRenameCancel = () => {
    setRenamingItemId(null);
    setRenameValue('');
  };

  const handleRenameSave = () => {
    if (renameValue.trim() !== '') {
      updateProductInQuote(renamingItemId, { jobName: renameValue.trim() });
    }
    setRenamingItemId(null);
    setRenameValue('');
  };

  const handleRenameKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRenameSave();
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  const handleSuccessClose = useCallback(
    (reason = 'auto_close') => {
      if (successCloseGuardRef.current) return;
      successCloseGuardRef.current = true;

      if (reason === 'open_upload' && submittedQuote?.id) {
        requestUploadForQuote(submittedQuote.id);
      }

      setIsSubmissionSuccessful(false);
      setSuccessMessage('');
      setSubmittedQuote(null);
      setIsSubmitting(false);

      if (reason === 'open_upload' && submittedQuote?.id) {
        onClose({ type: 'quote_submitted', quoteId: submittedQuote.id, action: 'open_upload_modal' });
      } else {
        onClose('quote_submitted');
      }
    },
    [onClose, requestUploadForQuote, submittedQuote]
  );

  const handleOpenUploadFromSuccess = () => {
    handleSuccessClose('open_upload');
  };

  // Handle submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentQuote.items.length === 0) {
      alert('No products in the quote to submit. Please add products to the quote first.');
      return;
    }

    if (!quoteName.trim() || !clientName.trim()) {
      alert('Please enter both quote name and client name');
      return;
    }

    if (!isDeliveryAddressValid()) {
      alert('Please complete the delivery address');
      return;
    }

    const triggeredByEnter = enterSubmitRef.current;
    enterSubmitRef.current = false;
    if (triggeredByEnter) {
      const confirmed = window.confirm('You pressed Enter. Submit this quote now?');
      if (!confirmed) {
        return;
      }
    }

    setIsSubmitting(true);
    const itemsToSubmit = [...currentQuote.items]; // Only submit items already in the quote

    // Create the delivery info object
    const deliveryInfo = {
      option: deliveryOption,
      cost: deliveryCost,
      boxesNeeded: deliveryOption === 'express' ? expressDeliveryDetails.boxesNeeded : 0,
      estimatedWeight: deliveryOption === 'express' ? expressDeliveryDetails.estimatedWeight : 0,
      hasOversizedItems: hasOversizedItems,
      address: deliveryOption === 'express' ? deliveryAddressText : null,
      numberOfAddresses: deliveryOption === 'multi-address' ? numberOfAddresses : null,
      addresses: deliveryOption === 'multi-address' ? manualAddresses : null,
      expressSubOption: deliveryOption === 'express' ? expressSubOption : null,
      expressSubOptionCost: deliveryOption === 'express' ? expressDeliveryDetails.expressSubOptionCost : 0,
    };

    // Create the quote object
    const quoteData = {
      quoteName,
      clientName,
      companyName,
      dateRequired, // Add dateRequired to quoteData
      notes,
      items: itemsToSubmit,
      totalProductsPrice,
      deliveryInfo,
      totalPrice: finalTotalPrice,
      // Preserve convertedFromDraft flag if it exists on currentQuote
      ...(currentQuote.convertedFromDraft && { convertedFromDraft: true })
    };

    // Submit the quote
    const result = await addQuote(quoteData);

    if (result.success) {
      successCloseGuardRef.current = false;
      setSubmittedQuote(result.quote || null);
      setIsSubmissionSuccessful(true);
      setSuccessMessage('Order submitted successfully!');
      setIsSubmitting(false);
    } else {
      alert('Error submitting quote: ' + result.error);
      setIsSubmitting(false);
    }

  };

  useEffect(() => {
    if (!isSubmissionSuccessful) {
      return;
    }

    setSuccessCountdown(10);
    successCloseGuardRef.current = false;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleSuccessClose('escape_key');
      }
    };

    const countdownInterval = setInterval(() => {
      setSuccessCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const autoCloseTimer = setTimeout(() => {
      handleSuccessClose('auto_close');
    }, 10000);

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(autoCloseTimer);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSuccessClose, isSubmissionSuccessful]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col relative">
        {/* Success Message Banner - Absolutely centered overlay with grow animation */}
        {isSubmissionSuccessful && successMessage && (
          <div className="absolute inset-0 flex items-center justify-center z-50 bg-white bg-opacity-95 rounded-lg animate-fadeIn">
            <div className="bg-green-50 border-4 border-green-600 text-green-900 rounded-lg p-6 shadow-2xl max-w-2xl animate-growIn">
              <div className="flex items-start gap-4">
                <svg className="w-10 h-10 mr-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="space-y-2">
                  <p className="font-bold text-xl">{successMessage}</p>
                  <p className="text-sm text-green-800">Thank you for your order. We'll review it and get back to you shortly.</p>
                  {submittedQuote?.id && (
                    <p className="text-sm text-green-900">
                      Want to upload artwork now?{' '}
                      <button
                        type="button"
                        onClick={handleOpenUploadFromSuccess}
                        className="underline font-semibold"
                      >
                        Go to My Orders
                      </button>{' '}
                      to open the upload window for this order.
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-green-800">
                    <span>Closing in {successCountdown}s</span>
                    <button
                      type="button"
                      onClick={() => handleSuccessClose('dismiss_button')}
                      className="px-3 py-1 bg-green-700 text-white rounded shadow hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      Dismiss
                    </button>
                    <span className="text-green-700">Press Esc to dismiss</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes growIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }

          .animate-growIn {
            animation: growIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .animate-fadeIn {
            animation: fadeIn 0.3s ease-in;
          }
        `}</style>

        {/* Scrollable content area */}
        <div className="p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Review Quote</h2>
            <button
              onClick={() => onClose('add_more_items')}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
              aria-label="Add More Items"
            >
              Add More Items
            </button>
          </div>

        {!displayForm ? ( // Show this if no items in quote
          <div className="text-center py-10">
            <p className="text-gray-500 mb-4">No products in your quote.</p>
            <button
              onClick={() => onClose(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Add Some Products
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.defaultPrevented) {
                const target = event.target;
                const tag = target?.tagName;
                const type = target?.type;
                const isTextarea = tag === 'TEXTAREA';
                const isButton = tag === 'BUTTON';
                const isSubmitInput = tag === 'INPUT' && (type === 'submit' || type === 'button');
                if (!isTextarea && !isButton && !isSubmitInput) {
                  enterSubmitRef.current = true;
                }
              }
            }}
          >
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4">Products</h3>
              
              {/* List of products already in the quote */}
              {currentQuote.items.length > 0 && (
                <div className="mb-4">
                  <div className="bg-gray-50 rounded-t border-t border-l border-r border-gray-200 p-3">
                    <div className="grid grid-cols-12 gap-2 font-medium text-sm">
                      <div className="col-span-6">Product</div>
                      <div className="col-span-2 text-center">Dimensions</div>
                      <div className="col-span-1 text-center">Qty</div>
                      <div className="col-span-2 text-right">Price</div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                  
                  <div className="border-l border-r border-gray-200">
                    {currentQuote.items.map((product, index) => {
                      const isGroupedPiece = Boolean(product.multiPieceGroupId);
                      const groupInfo = isGroupedPiece
                        ? multiPieceGroupMeta.get(product.multiPieceGroupId)
                        : null;
                      const showGroupHeader =
                        Boolean(groupInfo) && groupInfo.firstIndex === index;
                      const isLegacyMultiPiece =
                        !isGroupedPiece && Array.isArray(product.pieces) && product.pieces.length > 0;
                      const materialSummary =
                        (Array.isArray(product.materials) && product.materials.length > 0
                          ? product.materials
                              .map((material) => material.name || material.id || '')
                              .filter(Boolean)
                              .join(', ')
                          : undefined) ||
                        product.materialNames ||
                        product.material ||
                        'N/A';
                      const finishSummary =
                        Array.isArray(product.processes) && product.processes.length > 0
                          ? product.processes.join(', ')
                          : typeof product.processes === 'string'
                          ? product.processes
                          : '';
                      const multiPieceAdjustment =
                        typeof product.multiPieceAdjustment === 'number'
                          ? product.multiPieceAdjustment
                          : 0;

                      return (
                        <React.Fragment key={product.id}>
                          {showGroupHeader && (
                            <div className="bg-blue-50 px-3 py-2 border-b border-gray-200">
                              <div className="flex flex-col gap-1 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between">
                                <span className="font-medium">
                                  {groupInfo?.name || 'Multi-piece Group'} • {groupInfo?.count || 1}{' '}
                                  item{(groupInfo?.count || 1) !== 1 ? 's' : ''}
                                </span>
                                {groupInfo?.summary?.finalTotal != null && (
                                  <span className="text-blue-800 font-medium">
                                    Group total £{Number(groupInfo.summary.finalTotal).toFixed(2)}
                                  </span>
                                )}
                              </div>
                          {groupInfo &&
                            Math.abs(groupInfo?.summary?.adjustment ?? 0) > 0.005 && (
                              <p className="text-xs text-blue-800 mt-1">
                                Includes minimum charge adjustment of £
                                {(groupInfo.summary?.adjustment ?? 0).toFixed(2)} shared across this group.
                              </p>
                            )}
                            </div>
                          )}

                          <div
                            className={`grid grid-cols-12 gap-2 p-3 border-b border-gray-200 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }`}
                          >
                            <div className="col-span-6">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{product.jobName}</p>
                                <button
                                  type="button"
                                  onClick={(e) => handleRenameStart(e, product)}
                                  className="text-blue-600 hover:text-blue-800 text-xs opacity-70 hover:opacity-100"
                                  title="Rename"
                                >
                                  ✏️
                                </button>
                              </div>

                              {renamingItemId === product.id && (
                                <div className="flex items-center gap-2 mt-2">
                                  <input
                                    type="text"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onKeyDown={handleRenameKeyPress}
                                    onBlur={handleRenameSave}
                                    className="font-medium bg-white border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                                    placeholder="Enter new name..."
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRenameSave}
                                    className="text-green-600 hover:text-green-800 text-xs px-1"
                                    title="Save"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleRenameCancel}
                                    className="text-red-600 hover:text-red-800 text-xs px-1"
                                    title="Cancel"
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}

                              {isGroupedPiece && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Item {product.multiPieceGroupIndex} of {product.multiPieceGroupCount}
                                </p>
                              )}

                              {isLegacyMultiPiece ? (
                                <>
                                  <p className="text-sm text-gray-500">
                                    Pieces: {product.pieces.length}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Materials: {materialSummary}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm text-gray-500">{materialSummary}</p>
                                  {finishSummary && (
                                    <p className="text-sm text-gray-500">
                                      Finishing: {finishSummary}
                                    </p>
                                  )}
                                </>
                              )}

                              {product.productType === 'small_format' &&
                                product.isBooklet &&
                                product.separateCover &&
                                product.coverProcesses &&
                                product.coverProcesses.length > 0 && (
                                  <p className="text-sm text-gray-500">
                                    Cover Finishing: {product.coverProcesses.join(', ')}
                                  </p>
                                )}

                              {isGroupedPiece && Math.abs(multiPieceAdjustment) > 0.005 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Group allocation: £{multiPieceAdjustment.toFixed(2)}
                                </p>
                              )}
                            </div>
                            <div className="col-span-2 text-center text-sm">
                              {isGroupedPiece
                                ? product.dimensions || `${product.quantity} unit${product.quantity === 1 ? '' : 's'}`
                                : isLegacyMultiPiece
                                ? `${product.pieces.length} pcs`
                                : product.dimensions}
                            </div>
                            <div className="col-span-1 text-center text-sm">
                              {product.quantity}
                            </div>
                            <div className="col-span-2 text-right">
                              £{Number(product.price || 0).toFixed(2)}
                            </div>
                            <div className="col-span-1 text-right">
                              <button
                                type="button"
                                onClick={() => removeProductFromQuote(product.id)}
                                className="text-red-600 hover:text-red-800"
                                aria-label="Remove product"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Products subtotal */}
              <div className="flex justify-between items-center border-t border-gray-200 py-3 mb-4">
                <span className="font-medium">Products Subtotal:</span>
                <span className="font-medium">£{totalProductsPrice.toFixed(2)}</span>
              </div>
            </div>

            <p className="mb-6 text-xs text-gray-500">
              Prices are indicative and may change after artwork review.
            </p>

            {/* Delivery Options */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium mb-4">Delivery Options</h3>
              
              <div className="space-y-3">
                <div className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input 
                    type="radio" 
                    id="collection" 
                    name="deliveryOption"
                    checked={deliveryOption === 'collection'} 
                    onChange={() => setDeliveryOption('collection')}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <label htmlFor="collection" className="font-medium block">Collection (Free)</label>
                    <p className="text-sm text-gray-600">Collect from our premises during business hours</p>
                  </div>
                </div>
                
                <div className="border rounded hover:bg-gray-50">
                  <div className="flex items-start p-3">
                    <input
                      type="radio"
                      id="express"
                      name="deliveryOption"
                      checked={deliveryOption === 'express'}
                      onChange={() => {
                        setDeliveryOption('express');
                        if (deliveryOption !== 'express') {
                          setExpressSubOption(null);
                        }
                      }}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <label htmlFor="express" className="font-medium block">Express Delivery (£{expressDeliveryCost.toFixed(2)})</label>
                      <p className="text-sm text-gray-600">Next working day (order before 2pm)</p>
                      {expressDeliveryDetails.boxesNeeded > 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Estimated weight: {expressDeliveryDetails.estimatedWeight}kg
                          ({expressDeliveryDetails.boxesNeeded} boxes needed at £9.50 per box)
                        </p>
                      )}
                      {expressDeliveryDetails.expressSubOptionCost > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Includes £{expressDeliveryDetails.expressSubOptionCost.toFixed(2)} surcharge for delivery option
                        </p>
                      )}
                      {hasOversizedItems && (
                        <p className="text-xs text-amber-600 mt-1">
                          Includes £20 surcharge for oversized items ({'>'}1m)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Express sub-options */}
                  {deliveryOption === 'express' && (
                    <div className="pl-8 pr-3 pb-3 border-t border-gray-100">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-standard"
                            name="expressSubOption"
                            checked={expressSubOption === null}
                            onChange={() => setExpressSubOption(null)}
                            className="mr-2"
                          />
                          <label htmlFor="express-standard" className="text-sm">Standard next day (no extra charge)</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-pre12"
                            name="expressSubOption"
                            checked={expressSubOption === 'pre12'}
                            onChange={() => setExpressSubOption('pre12')}
                            className="mr-2"
                          />
                          <label htmlFor="express-pre12" className="text-sm">Pre 12:00 (+£{(expressDeliveryDetails.boxesNeeded * 6).toFixed(2)})</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-pre1030"
                            name="expressSubOption"
                            checked={expressSubOption === 'pre1030'}
                            onChange={() => setExpressSubOption('pre1030')}
                            className="mr-2"
                          />
                          <label htmlFor="express-pre1030" className="text-sm">Pre 10:30 (+£{(expressDeliveryDetails.boxesNeeded * 10).toFixed(2)})</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-saturday1030"
                            name="expressSubOption"
                            checked={expressSubOption === 'saturday1030'}
                            onChange={() => setExpressSubOption('saturday1030')}
                            className="mr-2"
                          />
                          <label htmlFor="express-saturday1030" className="text-sm">Saturday pre 10:30 (+£{(expressDeliveryDetails.boxesNeeded * 18).toFixed(2)})</label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input 
                    type="radio" 
                    id="multi-address" 
                    name="deliveryOption"
                    checked={deliveryOption === 'multi-address'} 
                    onChange={() => setDeliveryOption('multi-address')}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <label htmlFor="multi-address" className="font-medium block">Multi-Address Delivery</label>
                    <p className="text-sm text-gray-600">Deliver to multiple addresses (£9.50 per address)</p>
                    {deliveryOption === 'multi-address' && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <label htmlFor="numberOfAddresses" className="text-sm font-medium">Number of addresses:</label>
                          <input
                            type="number"
                            id="numberOfAddresses"
                            value={numberOfAddresses}
                            onChange={(e) => setNumberOfAddresses(Math.max(2, parseInt(e.target.value) || 2))}
                            min="2"
                            className="w-16 p-1 border rounded text-center"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Total cost: {numberOfAddresses} addresses × £9.50 = £{(numberOfAddresses * 9.5).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Delivery address section */}
              {deliveryOption === 'express' && (
                <div className="mt-4 p-4 bg-gray-50 rounded border">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">Delivery Address</h4>
                    <button
                      type="button"
                      onClick={() => setShowAddressBookModal(true)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Select from Address Book
                    </button>
                  </div>
                  
                  {/* Show selected customer info if address was selected from address book */}
                  {selectedAddress && (
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-sm">
                        <span className="font-medium">Selected Customer:</span> {selectedAddress.customer_name}
                        {selectedAddress.company_name && (
                          <span className="ml-2 text-gray-600">({selectedAddress.company_name})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAddress(null);
                            setDeliveryAddressText('');
                          }}
                          className="ml-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Delivery Address *</label>
                    <textarea
                      value={deliveryAddressText}
                      onChange={(e) => setDeliveryAddressText(e.target.value)}
                      className="w-full p-3 border rounded font-mono"
                      rows="10"
                      placeholder={`Enter the full delivery address - one line per field:

123 Main Street
Suite 4B
London
SW1A 1AA
Leave with reception if no answer`}
                      required={deliveryOption === 'express'}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the complete delivery address with each component on a separate line.
                      Include all relevant details such as street address, unit/suite, city, postcode, and any delivery instructions.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Multi-address delivery section */}
              {deliveryOption === 'multi-address' && (
                <div className="mt-4 p-4 bg-gray-50 rounded border">
                  <h4 className="font-medium mb-3">Multiple Delivery Addresses</h4>
                  
                  {numberOfAddresses <= 4 ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Please enter each delivery address on a separate line.
                      </p>
                      <div>
                        <label className="block text-sm font-medium mb-1">Delivery Addresses *</label>
                        <textarea
                          value={manualAddresses}
                          onChange={(e) => setManualAddresses(e.target.value)}
                          className="w-full p-3 border rounded"
                          rows="8"
                          placeholder="Enter each delivery address on a separate line:&#10;&#10;Jack Wilson, 123 Main St, City, Postcode&#10;Ben Smith, 456 Oak Ave, Town, Postcode&#10;Tom Jones, 789 Pine Rd, Village, Postcode"
                          required={deliveryOption === 'multi-address' && numberOfAddresses <= 4}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter {numberOfAddresses} addresses (one per line). Include full address details for each location.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mr-3">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h5 className="font-medium text-blue-900 mb-2">Document Upload Required</h5>
                          <p className="text-sm text-blue-800 mb-2">
                            For orders with more than 4 addresses, please upload a document containing all delivery addresses when you upload your artwork files.
                          </p>
                          <p className="text-sm text-blue-800">
                            <strong>Accepted formats:</strong> .txt, .docx, or .xlsx files<br />
                            <strong>Format:</strong> Include recipient name and full address for each location (one per line or row)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Quote information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6"> {/* Adjusted to 4 columns for date */}
              <div>
                <label className="block text-sm font-medium mb-1">Quote Name *</label>
                <input
                  type="text"
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="e.g. Event Materials Package"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  A descriptive name for this group of products
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Client Name *</label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter client name"
                  required
                />
              </div>
              <div> {/* New Company Name Field */}
                <label className="block text-sm font-medium mb-1">Company Name (Optional)</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter company name"
                />
              </div>
              <div> {/* New Date Required Field */}
                <label className="block text-sm font-medium mb-1">Date Required (Optional)</label>
                <input
                  type="date"
                  value={dateRequired}
                  onChange={(e) => setDateRequired(e.target.value)}
                  className="w-full p-2 border rounded"
                  // Optional: Add min attribute to prevent past dates
                  min={new Date().toISOString().split('T')[0]} 
                />
              </div>
            </div>


            
            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Additional Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
                placeholder="Any additional information for this quote"
              ></textarea>
            </div>
            
            {/* Total price with delivery */}
            <div className="flex justify-between items-center border-t border-b border-gray-200 py-3 mb-6">
              <div>
                <span className="font-bold text-lg">Total:</span>
                {deliveryCost > 0 && (
                  <div className="text-sm text-gray-600">
                    Products: £{totalProductsPrice.toFixed(2)}<br />
                    Delivery: £{deliveryCost.toFixed(2)}
                  </div>
                )}
              </div>
              <span className="font-bold text-lg">£{finalTotalPrice.toFixed(2)}</span>
            </div>
            
            {/* Actions - Hide when submission is successful */}
            {!isSubmissionSuccessful && (
              <div className="flex justify-between">
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      clearCurrentQuote(); // Clear the quote items immediately
                      onClose('clear_all'); // Then close modal and let parent handle form reset
                    }}
                    className="px-4 py-2 text-red-600 border border-red-600 rounded mr-2"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => onClose('add_more_items')}
                    className="px-4 py-2 border rounded"
                  >
                    Add More Items
                  </button>
                </div>

                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-green-400"
                  disabled={isSubmitting || currentQuote.items.length === 0 || !isDeliveryAddressValid()}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Order'}
                </button>
              </div>
            )}
          </form>
        )}
        </div> {/* End scrollable content area */}

        {/* Address Book Modal */}
        <AddressBookModal
          isOpen={showAddressBookModal}
          onClose={() => setShowAddressBookModal(false)}
          onSelectAddress={handleAddressSelected}
          onAddNewAddress={handleAddNewAddress}
        />
      </div>
    </div>
  );
};

export default QuoteReview;
